import {
  IconAbc,
  IconAlignLeft,
  IconAt,
  IconCalendar,
  IconCalendarTime,
  IconCheckbox,
  IconChevronDown,
  IconCircleDot,
  IconClock,
  IconEyeOff,
  IconLink,
  IconNumber,
  IconPhone,
  IconSend,
  IconUpload,
} from '@tabler/icons-react';
import type React from 'react';

// Canonical 15 backend field types — must mirror VALID_FIELD_TYPES in
// backend/formshive/src/api/core_digest/spec.rs
export type FieldType =
  | 'text'
  | 'textarea'
  | 'email'
  | 'number'
  | 'url'
  | 'tel'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'date'
  | 'datetime'
  | 'time'
  | 'file'
  | 'hidden'
  | 'submit';

export type FieldCategory = 'standard' | 'choices' | 'other';

export interface FieldTypeMeta {
  type: FieldType;
  category: FieldCategory;
  labelKey: string;
  icon: React.ReactNode;
  hasOptions?: boolean;
  hasPlaceholder?: boolean;
}

export const FIELD_TYPES: FieldTypeMeta[] = [
  {
    type: 'text',
    category: 'standard',
    labelKey: 'fieldType.text',
    icon: <IconAbc size={20} />,
    hasPlaceholder: true,
  },
  {
    type: 'textarea',
    category: 'standard',
    labelKey: 'fieldType.textarea',
    icon: <IconAlignLeft size={20} />,
    hasPlaceholder: true,
  },
  {
    type: 'email',
    category: 'standard',
    labelKey: 'fieldType.email',
    icon: <IconAt size={20} />,
    hasPlaceholder: true,
  },
  {
    type: 'number',
    category: 'standard',
    labelKey: 'fieldType.number',
    icon: <IconNumber size={20} />,
    hasPlaceholder: true,
  },
  {
    type: 'url',
    category: 'standard',
    labelKey: 'fieldType.url',
    icon: <IconLink size={20} />,
    hasPlaceholder: true,
  },
  {
    type: 'tel',
    category: 'standard',
    labelKey: 'fieldType.tel',
    icon: <IconPhone size={20} />,
    hasPlaceholder: true,
  },
  {
    type: 'select',
    category: 'choices',
    labelKey: 'fieldType.select',
    icon: <IconChevronDown size={20} />,
    hasOptions: true,
  },
  {
    type: 'radio',
    category: 'choices',
    labelKey: 'fieldType.radio',
    icon: <IconCircleDot size={20} />,
    hasOptions: true,
  },
  {
    type: 'checkbox',
    category: 'choices',
    labelKey: 'fieldType.checkbox',
    icon: <IconCheckbox size={20} />,
    hasOptions: true,
  },
  {
    type: 'date',
    category: 'other',
    labelKey: 'fieldType.date',
    icon: <IconCalendar size={20} />,
    hasPlaceholder: true,
  },
  {
    type: 'datetime',
    category: 'other',
    labelKey: 'fieldType.datetime',
    icon: <IconCalendarTime size={20} />,
    hasPlaceholder: true,
  },
  {
    type: 'time',
    category: 'other',
    labelKey: 'fieldType.time',
    icon: <IconClock size={20} />,
    hasPlaceholder: true,
  },
  { type: 'file', category: 'other', labelKey: 'fieldType.file', icon: <IconUpload size={20} /> },
  {
    type: 'hidden',
    category: 'other',
    labelKey: 'fieldType.hidden',
    icon: <IconEyeOff size={20} />,
  },
  { type: 'submit', category: 'other', labelKey: 'fieldType.submit', icon: <IconSend size={20} /> },
];

export const getFieldTypeMeta = (type: string): FieldTypeMeta | undefined =>
  FIELD_TYPES.find((t) => t.type === type);

export const isValidFieldType = (type: string): type is FieldType =>
  FIELD_TYPES.some((t) => t.type === type);
